import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import "./MyProfile.css";
import { useLanguage } from "../../../utils/languageContext";

/* ------------------------------------------------------------------ */
/* Design tokens — sidebar / e-OPD module se match karte hain          */
/* ------------------------------------------------------------------ */
const T = {
  ink: "#111C33",
  label: "#2C3A57",
  muted: "#5A657D",
  faint: "#8A94A6",
  blue: "#1E88E5",
  blueDark: "#1565C0",
  blueSoft: "#E8F3FE",
  border: "#D8DFEA",
  hairline: "#E3E8F0",
  surface: "#F7F9FC",
};

const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

/** Icon + title wala outlined card — baaki module jaisa hi. */
function SectionCard({ icon, title, count, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        borderColor: T.border,
        backgroundColor: "#fff",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{
          px: 2.25,
          py: 1.5,
          backgroundColor: T.surface,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <Box sx={{ display: "flex", color: T.blue, "& svg": { fontSize: 18 } }}>{icon}</Box>
        <Typography
          sx={{
            flex: 1,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: ".06em",
            textTransform: "uppercase",
            color: T.ink,
          }}
        >
          {title}
        </Typography>
        {count > 0 && (
          <Chip
            label={count}
            size="small"
            sx={{
              height: 20,
              minWidth: 24,
              fontSize: 11,
              fontWeight: 700,
              color: T.blue,
              backgroundColor: T.blueSoft,
            }}
          />
        )}
      </Stack>
      <Box sx={{ p: 2.25 }}>{children}</Box>
    </Paper>
  );
}

/** Label upar, value neeche — pehle dono card ke do kinaron par the. */
function DetailRow({ label, value }) {
  const empty = !value || value === "N/A";
  return (
    <Box
      sx={{
        py: 1.25,
        borderBottom: `1px dashed ${T.hairline}`,
        "&:first-of-type": { pt: 0 },
        "&:last-of-type": { pb: 0, borderBottom: 0 },
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: ".05em",
          textTransform: "uppercase",
          color: T.muted,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: empty ? 400 : 600,
          color: empty ? T.faint : T.ink,
          wordBreak: "break-word",
        }}
      >
        {empty ? "N/A" : value}
      </Typography>
    </Box>
  );
}

export const MyProfile = () => {
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [connectedPrograms, setConnectedPrograms] = useState([]);

  useEffect(() => {
    const userData = localStorage.getItem("loggedinUserdetails");
    if (userData) {
      setUser(JSON.parse(userData));
    }

    // Get all connected programs
    const programs = [];

    if (localStorage.getItem("NCD_token")) {
      programs.push({
        name: "NCD",
        logo: "/images/programs_logo/ncd.png",
        username: localStorage.getItem("NCD_loggedinUserdetails")
          ? JSON.parse(localStorage.getItem("NCD_loggedinUserdetails")).username
          : "N/A",
      });
    }

    if (localStorage.getItem("sickle_token")) {
      programs.push({
        name: "Sickle Cell",
        logo: "/images/programs_logo/sickle.png",
        username: localStorage.getItem("SICKLE_loggedinUserdetails")
          ? JSON.parse(localStorage.getItem("SICKLE_loggedinUserdetails"))
              .username
          : "N/A",
      });
    }

    if (localStorage.getItem("PMNDP_token")) {
      programs.push({
        name: "PMNDP",
        logo: "/images/programs_logo/pmndp.png",
        username: localStorage.getItem("PMNDP_loggedinUserdetails")
          ? JSON.parse(localStorage.getItem("PMNDP_loggedinUserdetails"))
              .username
          : "N/A",
      });
    }

    if (localStorage.getItem("NIKSHAY_valid_user")) {
      programs.push({
        name: "Nikshay",
        logo: "/images/programs_logo/nikshay.png",
        username: localStorage.getItem("NIKSHAY_loggedinUserdetails")
          ? JSON.parse(localStorage.getItem("NIKSHAY_loggedinUserdetails"))
              .username
          : "N/A",
      });
    }

    if (localStorage.getItem("uwin_token")) {
      programs.push({
        name: "U-WIN",
        logo: "/images/programs_logo/uwin.png",
        username: localStorage.getItem("uwin_loggedinUserdetails")
          ? JSON.parse(localStorage.getItem("uwin_loggedinUserdetails"))
              .username
          : "N/A",
      });
    }

    setConnectedPrograms(programs);
  }, []);

  const displayName = user?.firstname || "User Name";
  const roleName = user?.rolename || "Role";

  return (
    <div className="profile-page">
      {/* -------- Page header -------- */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: T.ink, lineHeight: 1.25 }}>
          {t("menu.myProfile")}
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: T.muted, mt: 0.5 }}>
          Your account details and the health programs linked to it.
        </Typography>
      </Box>

      {/* -------- Identity banner -------- */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${T.border}`,
          overflow: "hidden",
          mb: 2.5,
          background: "linear-gradient(135deg, #F4F9FF 0%, #EDF3FF 100%)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={2}
          sx={{ px: { xs: 2.5, md: 3.5 }, py: { xs: 2.5, md: 3 } }}
        >
          <Avatar
            sx={{
              width: 64,
              height: 64,
              fontSize: 22,
              fontWeight: 700,
              color: "#fff",
              background: `linear-gradient(135deg, ${T.blue} 0%, ${T.blueDark} 100%)`,
              flexShrink: 0,
            }}
          >
            {initialsOf(displayName)}
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 19, fontWeight: 700, color: T.ink, lineHeight: 1.25 }}>
              {displayName}
            </Typography>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
              <Chip
                label={roleName}
                size="small"
                sx={{
                  height: 24,
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: "#1565C0",
                  backgroundColor: "rgba(255,255,255,.85)",
                  border: "1px solid rgba(30,136,229,.18)",
                }}
              />
              {user?.username && (
                <Chip
                  label={`@${user.username}`}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: T.muted,
                    backgroundColor: "rgba(255,255,255,.85)",
                    border: `1px solid ${T.border}`,
                  }}
                />
              )}
            </Stack>

            {user?.facilitydetail && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1.25 }}>
                <PlaceOutlinedIcon sx={{ fontSize: 15, color: T.muted }} />
                <Typography sx={{ fontSize: 12.5, color: T.label, fontWeight: 500 }}>
                  {user.facilitydetail}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* -------- Details -------- */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard icon={<BadgeOutlinedIcon />} title="Account Details">
            <DetailRow label="Primary Username" value={user?.username} />
            <DetailRow label="Role" value={user?.rolename} />
            <DetailRow label="Mobile" value={user?.InchargeMobile} />
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard icon={<PlaceOutlinedIcon />} title="Facility & Location">
            <DetailRow label="Facility" value={user?.facilitydetail} />
            <DetailRow label="State Code" value={user?.stateCode} />
          </SectionCard>
        </Grid>
      </Grid>

      {/* -------- Connected programs -------- */}
      <SectionCard
        icon={<AppsOutlinedIcon />}
        title="Connected Programs"
        count={connectedPrograms.length}
      >
        {connectedPrograms.length === 0 ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
            <LinkOffRoundedIcon sx={{ fontSize: 32, color: "#B4BCCB" }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.label }}>
              No programs connected
            </Typography>
            <Typography sx={{ fontSize: 13, color: T.muted, textAlign: "center" }}>
              Sign in to a health program and it will appear here.
            </Typography>
          </Stack>
        ) : (
          <Grid container spacing={1.5}>
            {connectedPrograms.map((program, index) => (
              <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    height: "100%",
                    borderRadius: 2,
                    borderColor: T.border,
                    transition: "border-color .2s ease, background-color .2s ease",
                    "&:hover": { borderColor: T.blue, backgroundColor: T.blueSoft },
                  }}
                >
                  <Avatar
                    src={program.logo}
                    alt={program.name}
                    variant="rounded"
                    sx={{
                      width: 38,
                      height: 38,
                      flexShrink: 0,
                      fontSize: 13,
                      fontWeight: 700,
                      color: T.blueDark,
                      backgroundColor: "#fff",
                      border: `1px solid ${T.border}`,
                      "& img": { objectFit: "contain", p: "3px" },
                    }}
                  >
                    {initialsOf(program.name)}
                  </Avatar>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: 13.5, fontWeight: 700, color: T.ink, lineHeight: 1.3 }}
                    >
                      {program.name}
                    </Typography>
                    <Typography
                      title={program.username}
                      sx={{
                        fontSize: 12,
                        color: T.muted,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {program.username}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </SectionCard>

      <Typography
        sx={{
          mt: 3,
          fontSize: 11.5,
          color: T.faint,
          textAlign: "center",
        }}
      >
        © All rights reserved by Ministry of Health &amp; Family Welfare, Government of India.
      </Typography>
    </div>
  );
};
